import { extractDomainFromEmail, getLocaleFromUrl } from 'utils';
import { debounce } from '../util/debounce';
import {
  AccordionClickEvent,
  AccordionClickEventProperties,
  AccordionEvent,
  AccordionEventProperties,
  AddFavoriteEvent,
  AssetActionEvent,
  AssetActionEventProperties,
  DataLayerEvent,
  DataLayerInitializedProperties,
  FilterEventProps,
  NavigationActionEvent,
  NavigationActionEventProperties,
  ProductClickEvent,
  ProductDetailsEvent,
  ProductEventProperties,
  ProductListActionEvent,
  ProductListActionEventProperties,
  ProductsImpressionEvent,
  PromoClickEvent,
  PromoClickEventProperties,
  PromoViewEvent,
  PromoViewEventProperties,
  PublisherEventProperties,
  PublisherPageViewEvent,
  PublisherVisitedEvent,
  ReviewActionEvent,
  ReviewActionEventProperties,
  SearchClickEventProps,
  SearchEventCommonProps,
} from './analyticsEventTypes';
import impressionCollection from './impressionCollection';

declare global {
  // eslint-disable-next-line prefer-type-alias/prefer-type-alias
  interface Window {
    // Must match @unity/commerce-core's global Window.dataLayer augmentation
    // exactly (required, Record<string, unknown>[]) or declaration merging fails.
    dataLayer: Record<string, unknown>[];
    isAnalyticsInitialized?: boolean;
  }
}

type AssetActionProps = Pick<
  AssetActionEventProperties,
  'asset_id' | 'asset_name'
>;

type ProductDetailsProps = {
  id: string;
  name?: string;
  price?: string | number;
  brand?: string;
  category?: string;
  variant?: string;
};

type ProductImpressionProps = {
  name: string;
  id: string;
  price?: string | number;
  brand?: string;
  category?: string;
  variant?: string;
  position?: number;
};

function pushDataLayerEvent(event: DataLayerEvent) {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  window.dataLayer.push(event);
}

function pushPublisherPageViewEvent(
  publisherId: string,
  publisherName: string
) {
  const event: PublisherPageViewEvent = {
    event: 'pageview',
    page: `/publisher/${publisherId}`,
    title: publisherName,
  };
  pushDataLayerEvent(event);
}

function pushPublisherProductsImpressionEvent(
  products: Array<{
    id: string;
    name: string;
    brand: string;
    category: string;
    position: number;
    price: string | number;
  }>
) {
  products.forEach((product) => {
    impressionCollection.pushItem(product);
  });
}

function pushPublisherVisitedEvent(props: PublisherEventProperties) {
  const event: PublisherVisitedEvent = {
    event: 'userEvent',
    event_name: 'publisher_visited',
    properties: props,
  };
  pushDataLayerEvent(event);
}

function initialize(
  userId: undefined | string,
  email: undefined | string,
  currency: string
) {
  const eventProperties: DataLayerInitializedProperties = {
    event: 'dataLayer-initialized',
    user: {
      user_unity_id: userId,
      user_logged_in: userId ? 'yes' : 'no',
      user_email_domain: email ? extractDomainFromEmail(email) : undefined,
    },
    environment: {
      environment_locale: getLocaleFromUrl(),
      environment_currency: currency,
    },
  };
  pushDataLayerEvent(eventProperties);
  window.isAnalyticsInitialized = true;
}

function pushSearchEvent(
  eventProperties: SearchEventCommonProps | SearchClickEventProps
) {
  pushDataLayerEvent({
    event: 'userEvent',
    event_name: 'internal_search',
    properties: eventProperties,
  });
}

/**
 * Exported as a debounced function, since Coveo analyticsClientMiddleware will get triggered twice for every search event and we don't want to track duplicated events.
 * https://connect.coveo.com/s/article/18262
 */
const debouncedSearchEvent = debounce(pushSearchEvent, 1000);

function pushFilterEvent(eventProperties: FilterEventProps) {
  pushDataLayerEvent({
    event: 'userEvent',
    event_name: 'filter_action',
    properties: eventProperties,
  });
}

/**
 * Exported as a debounced function, since Coveo analyticsClientMiddleware will get triggered twice for every search event and we don't want to track duplicated events.
 * https://connect.coveo.com/s/article/18262
 */
const debouncedFilterEvent = debounce(pushFilterEvent, 1000);

function pushProductClickEvent(
  productEventProperties: ProductEventProperties,
  currency: string
) {
  const productClickEvent: ProductClickEvent = {
    event: 'ecomm',
    event_name: 'product_click',
    properties: {
      ecommerce_currency: currency,
      ecommerce_store: 'assetstore',
      products: [productEventProperties],
    },
  };
  pushDataLayerEvent(productClickEvent);
}

function pushAccordionClickEvent(
  accordionEventProperties: AccordionEventProperties
) {
  const accordionClickEvent: AccordionEvent = {
    event: 'userEvent',
    event_name: 'FAQ_action',
    properties: accordionEventProperties,
  };
  pushDataLayerEvent(accordionClickEvent);
}

function pushProductAccordionClickEvent(
  accordionEventProperties: AccordionClickEventProperties
) {
  const accordionClickEvent: AccordionClickEvent = {
    event: 'userEvent',
    event_name: 'accordion_click',
    properties: accordionEventProperties,
  };
  pushDataLayerEvent(accordionClickEvent);
}

function pushPromoViewEvent(promoEventProperties: PromoViewEventProperties) {
  const accordionClickEvent: PromoViewEvent = {
    event: 'promos',
    event_name: 'promo_view',
    properties: promoEventProperties,
  };
  pushDataLayerEvent(accordionClickEvent);
}

function pushPromoClickEvent(promoEventProperties: PromoClickEventProperties) {
  const accordionClickEvent: PromoClickEvent = {
    event: 'promos',
    event_name: 'promo_click',
    properties: promoEventProperties,
  };
  pushDataLayerEvent(accordionClickEvent);
}

function pushProductDetailsEvent(props: ProductDetailsProps) {
  const event: ProductDetailsEvent = {
    event: 'productDetails',
    ecommerce: {
      detail: {
        products: [
          {
            ...props,
            quantity: 1,
          },
        ],
      },
    },
  };
  pushDataLayerEvent(event);
}

function pushAssetActionEvent(props: AssetActionEventProperties) {
  const event: AssetActionEvent = {
    event: 'userEvent',
    event_name: 'asset_action',
    properties: props,
  };
  pushDataLayerEvent(event);
}

function pushAddToMyAssetEvent(props: AssetActionProps) {
  pushAssetActionEvent({
    ...props,
    asset_action: 'add_to_my_assets',
  });
}

function pushBuyNowEvent(props: AssetActionProps) {
  pushAssetActionEvent({ ...props, asset_action: 'buy_now' });
}

function pushRequestSeatEvent(props: AssetActionProps) {
  pushAssetActionEvent({ ...props, asset_action: 'request_seat' });
}

function pushManageSeatsEvent(props: AssetActionProps) {
  pushAssetActionEvent({ ...props, asset_action: 'manage_seats' });
}

function pushOpenInUnityEvent(props: AssetActionProps) {
  pushAssetActionEvent({
    ...props,
    asset_action: 'open_in_editor',
  });
}

function pushSaveToListEvent(props: AssetActionProps) {
  pushAssetActionEvent({ ...props, asset_action: 'save_to_list' });
}

function pushRemoveFromListEvent(props: AssetActionProps) {
  pushAssetActionEvent({ ...props, asset_action: 'remove_from_list' });
}

function pushAddFavoriteEvent(productName: string) {
  const event: AddFavoriteEvent = {
    event: 'addFavorite',
    ecommerce: { favoriteProduct: productName },
  };
  pushDataLayerEvent(event);
}

function pushProductsImpressionEvent(products: Array<ProductImpressionProps>) {
  const event: ProductsImpressionEvent = {
    event: 'productsImpression',
    ecommerce: {
      impressions: products,
    },
  };
  pushDataLayerEvent(event);
}

function pushReviewActionEvent(props: ReviewActionEventProperties) {
  const event: ReviewActionEvent = {
    event: 'userEvent',
    event_name: 'review_action',
    properties: props,
  };
  pushDataLayerEvent(event);
}

function pushOpenReviewModalEvent(
  props: Omit<ReviewActionEventProperties, 'review_action'>
) {
  pushReviewActionEvent({
    ...props,
    review_action: 'open_review_modal',
  });
}

function pushAddReviewEvent(
  props: Omit<ReviewActionEventProperties, 'review_action'>
) {
  pushReviewActionEvent({
    ...props,
    review_action: 'add',
  });
}

function pushCancelReviewSubmissionEvent(
  props: Omit<ReviewActionEventProperties, 'review_action'>
) {
  pushReviewActionEvent({
    ...props,
    review_action: 'cancel_review_submission',
  });
}

function pushContactPublisherEvent(
  props: Omit<ReviewActionEventProperties, 'review_action'>
) {
  pushReviewActionEvent({
    ...props,
    review_action: 'clicks_contact_publisher',
  });
}

function pushMarkContentAsHelpfulEvent(
  props: Omit<ReviewActionEventProperties, 'review_action'>
) {
  pushReviewActionEvent({
    ...props,
    review_action: 'mark_content_as_helpful',
  });
}

function pushMarkContentAsUnhelpfulEvent(
  props: Omit<ReviewActionEventProperties, 'review_action'>
) {
  pushReviewActionEvent({
    ...props,
    review_action: 'mark_content_as_unhelpful',
  });
}

function pushReportReviewEvent(
  props: Omit<ReviewActionEventProperties, 'review_action'>
) {
  pushReviewActionEvent({
    ...props,
    review_action: 'report_review',
  });
}

function pushProductListActionEvent(props: ProductListActionEventProperties) {
  const event: ProductListActionEvent = {
    event: 'userEvent',
    event_name: 'product_list_action',
    properties: props,
  };
  pushDataLayerEvent(event);
}

function pushCreateListEvent(
  props: Omit<ProductListActionEventProperties, 'product_list_action'>
) {
  pushProductListActionEvent({ ...props, product_list_action: 'create_list' });
}

function pushEditListEvent(
  props: Omit<ProductListActionEventProperties, 'product_list_action'>
) {
  pushProductListActionEvent({ ...props, product_list_action: 'edit_list' });
}

function pushDeleteListEvent(
  props: Omit<ProductListActionEventProperties, 'product_list_action'>
) {
  pushProductListActionEvent({ ...props, product_list_action: 'delete_list' });
}

function pushShareListEvent(
  props: Omit<ProductListActionEventProperties, 'product_list_action'>
) {
  pushProductListActionEvent({ ...props, product_list_action: 'share_list' });
}

function pushNavigationActionEvent(props: NavigationActionEventProperties) {
  const event: NavigationActionEvent = {
    event: 'userEvent',
    event_name: 'navigation_action',
    properties: props,
  };
  pushDataLayerEvent(event);
}

function pushReviewFilterEvent(props: FilterEventProps) {
  pushFilterEvent(props);
}

function pushReviewSortEvent(props: FilterEventProps) {
  pushFilterEvent(props);
}

export const analytics = {
  initialize,
  pushSearchEvent,
  debouncedSearchEvent,
  pushFilterEvent,
  debouncedFilterEvent,
  pushProductClickEvent,
  pushAccordionClickEvent,
  pushPromoViewEvent,
  pushPromoClickEvent,
  pushProductDetailsEvent,
  pushAddToMyAssetEvent,
  pushBuyNowEvent,
  pushRequestSeatEvent,
  pushManageSeatsEvent,
  pushOpenInUnityEvent,
  pushProductsImpressionEvent,
  pushProductAccordionClickEvent,
  pushPublisherVisitedEvent,
  pushPublisherPageViewEvent,
  pushPublisherProductsImpressionEvent,
  pushAssetActionEvent,
  pushReviewActionEvent,
  pushOpenReviewModalEvent,
  pushAddReviewEvent,
  pushCancelReviewSubmissionEvent,
  pushContactPublisherEvent,
  pushMarkContentAsHelpfulEvent,
  pushMarkContentAsUnhelpfulEvent,
  pushReportReviewEvent,
  pushNavigationActionEvent,
  pushReviewFilterEvent,
  pushReviewSortEvent,
  pushProductListActionEvent,
  pushSaveToListEvent,
  pushRemoveFromListEvent,
  pushAddFavoriteEvent,
  pushCreateListEvent,
  pushEditListEvent,
  pushDeleteListEvent,
  pushShareListEvent,
};
